/**
 * Adams Optics — Checkout Stock Validator
 *
 * Intercepts any checkout button click, checks real-time stock via
 * the Cloudflare Worker, and blocks checkout if any cart item is out of stock.
 *
 * The Worker also updates Shopify inventory in the background.
 */

(function () {
  const WORKER_URL    = 'https://adams-stock-checker.adamssync.workers.dev';
  const WORKER_SECRET = 'adams-sync-26';

  // ── UI helpers ──────────────────────────────────────────────────────────────

  function showError(message) {
    let banner = document.getElementById('adams-stock-error');
    if (!banner) {
      banner = document.createElement('div');
      banner.id = 'adams-stock-error';
      banner.style.cssText = [
        'position:fixed', 'top:20px', 'left:50%', 'transform:translateX(-50%)',
        'background:#c0392b', 'color:#fff', 'padding:14px 24px', 'border-radius:8px',
        'font-size:15px', 'font-weight:600', 'z-index:99999', 'text-align:center',
        'box-shadow:0 4px 16px rgba(0,0,0,0.2)', 'max-width:90vw',
      ].join(';');
      document.body.appendChild(banner);
    }
    banner.textContent = message;
    banner.style.display = 'block';
    clearTimeout(banner._timeout);
    banner._timeout = setTimeout(() => { banner.style.display = 'none'; }, 6000);
  }

  function setButtonLoading(btn, loading) {
    if (!btn) return;
    if (loading) {
      btn.dataset.originalText = btn.textContent;
      btn.textContent = 'Checking availability...';
      btn.disabled = true;
    } else {
      btn.textContent = btn.dataset.originalText || btn.textContent;
      btn.disabled = false;
    }
  }

  // ── Contact lens detection ───────────────────────────────────────────────────

  const contactLensHandleCache = {};

  function getProductHandleFromCartItem(item) {
    if (!item?.url) return null;
    const match = item.url.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function hasContactLensProperties(item) {
    const props = item.properties || {};
    return !!(
      props['Right Eye Power'] ||
      props['Left Eye Power'] ||
      props['_product_class'] === 'contact-lenses'
    );
  }

  async function isContactLensCartItem(item) {
    if (hasContactLensProperties(item)) return true;

    const handle = getProductHandleFromCartItem(item);
    if (!handle) return false;

    if (contactLensHandleCache[handle] !== undefined) {
      return contactLensHandleCache[handle];
    }

    try {
      const res = await fetch('/products/' + handle + '.js');
      if (!res.ok) {
        contactLensHandleCache[handle] = false;
        return false;
      }
      const product = await res.json();
      const tags = (product.tags || '')
        .split(',')
        .map(function (t) { return t.trim().toLowerCase(); });
      const isLens = tags.indexOf('contact-lenses') !== -1 || tags.indexOf('contact-lens') !== -1;
      contactLensHandleCache[handle] = isLens;
      return isLens;
    } catch {
      contactLensHandleCache[handle] = false;
      return false;
    }
  }

  function isContactLensProductPage() {
    return !!document.querySelector('[data-contact-lens-power]');
  }

  // ── Cart helpers ────────────────────────────────────────────────────────────

  async function getCartFrameIds() {
    const res  = await fetch('/cart.js');
    const cart = await res.json();

    const frameIds = [];
    for (const item of cart.items) {
      if (await isContactLensCartItem(item)) continue;

      const sku = item.sku;
      if (!sku) continue;
      const num = Number(sku);
      if (!isNaN(num) && num > 0) frameIds.push(num);
    }
    return frameIds;
  }

  // ── Product-page helpers (for Buy it now) ───────────────────────────────────

  function getSelectedVariantId(btn) {
    // Walk up from the button to find the product form
    const form = btn.closest('form') ||
                 btn.closest('[data-shopify="payment-button"]')
                   ?.closest('form, [data-product-form]') ||
                 document.querySelector('form[action*="/cart/add"]');
    if (!form) return null;
    const input = form.querySelector('input[name="id"]');
    return input ? input.value : null;
  }

  function getVariantSku(variantId) {
    // Primary: Shopify's own analytics object (available on all product pages)
    try {
      const variants = window.ShopifyAnalytics?.meta?.product?.variants;
      if (variants) {
        const v = variants.find(v => String(v.id) === String(variantId));
        if (v?.sku) return v.sku;
      }
    } catch {}

    // Fallback: look for a product JSON <script> tag injected by the theme
    const tags = document.querySelectorAll('script[type="application/json"]');
    for (const tag of tags) {
      try {
        const data = JSON.parse(tag.textContent);
        // Could be the product object or a wrapped object
        const product = data.product || data;
        if (Array.isArray(product.variants)) {
          const v = product.variants.find(v => String(v.id) === String(variantId));
          if (v?.sku) return v.sku;
        }
      } catch {}
    }
    return null;
  }

  // ── Stock check ─────────────────────────────────────────────────────────────

  async function checkStock(frameIds) {
    const controller = new AbortController();
    const timeout    = setTimeout(() => controller.abort(), 5000); // 5s timeout

    try {
      const res = await fetch(WORKER_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Worker-Secret': WORKER_SECRET },
        body:    JSON.stringify({ frames: frameIds }),
        signal:  controller.signal,
      });
      clearTimeout(timeout);
      if (!res.ok) return null; // fail open
      return await res.json();
    } catch {
      clearTimeout(timeout);
      return null; // timeout or network error — fail open
    }
  }

  // ── Checkout intercept ──────────────────────────────────────────────────────

  async function handleCheckout(event, btn) {
    event.preventDefault();
    event.stopPropagation();

    setButtonLoading(btn, true);

    let frameIds;
    try {
      frameIds = await getCartFrameIds();
    } catch {
      // Can't read cart — allow checkout
      if (btn) { event.target.submit ? event.target.submit() : btn.form?.submit(); }
      return;
    }

    // If no integer-SKU items in cart, allow checkout immediately
    if (frameIds.length === 0) {
      setButtonLoading(btn, false);
      proceedToCheckout(event, btn);
      return;
    }

    const data = await checkStock(frameIds);
    setButtonLoading(btn, false);

    // Fail open: if Worker is unreachable or errors, allow checkout
    if (!data || data.failOpen) {
      proceedToCheckout(event, btn);
      return;
    }

    const outOfStock = data.frames.filter(f => f.stock === 0);

    if (outOfStock.length > 0) {
      showError(
        outOfStock.length === 1
          ? 'Sorry, one item in your cart is currently out of stock. Please remove it to continue.'
          : `Sorry, ${outOfStock.length} items in your cart are currently out of stock. Please remove them to continue.`
      );
      return; // block checkout
    }

    // All in stock — proceed
    proceedToCheckout(event, btn);
  }

  function proceedToCheckout(event, btn) {
    const form = btn ? btn.closest('form') : (event.target.tagName === 'FORM' ? event.target : null);
    if (form) {
      // Remove our listener to avoid infinite loop, then submit
      form.removeEventListener('submit', formSubmitHandler);
      form.submit();
    } else {
      window.location.href = '/checkout';
    }
  }

  // ── Buy it now handler ──────────────────────────────────────────────────────

  // Flag to let the real click through after validation passes
  let _buyItNowBypassed = false;

  async function handleBuyItNow(event, btn) {
    if (_buyItNowBypassed) return; // already validated — let through

    event.preventDefault();
    event.stopImmediatePropagation();

    setButtonLoading(btn, true);

    if (isContactLensProductPage()) {
      setButtonLoading(btn, false);
      _proceedBuyItNow(btn);
      return;
    }

    const variantId = getSelectedVariantId(btn);
    const sku       = variantId ? getVariantSku(variantId) : null;
    const frameId   = sku && !isNaN(Number(sku)) && Number(sku) > 0 ? Number(sku) : null;

    if (!frameId) {
      // Can't identify SKU — fail open
      setButtonLoading(btn, false);
      _proceedBuyItNow(btn);
      return;
    }

    const data = await checkStock([frameId]);
    setButtonLoading(btn, false);

    if (!data || data.failOpen) {
      _proceedBuyItNow(btn);
      return;
    }

    const outOfStock = data.frames.filter(f => f.stock === 0);
    if (outOfStock.length > 0) {
      showError('Sorry, this item is currently out of stock.');
      return;
    }

    _proceedBuyItNow(btn);
  }

  function _proceedBuyItNow(btn) {
    _buyItNowBypassed = true;
    btn.click(); // fire the real Shopify click
    setTimeout(() => { _buyItNowBypassed = false; }, 300);
  }

  function buyItNowClickHandler(event) {
    handleBuyItNow(event, event.currentTarget);
  }

  // ── Attach listeners ────────────────────────────────────────────────────────

  function formSubmitHandler(event) {
    const btn = event.submitter || event.target.querySelector('[name="checkout"]');
    handleCheckout(event, btn);
  }

  function attachListeners() {
    // Cart forms (main cart page and cart drawer)
    document.querySelectorAll('form[action*="/checkout"]').forEach(form => {
      form.removeEventListener('submit', formSubmitHandler);
      form.addEventListener('submit', formSubmitHandler);
    });

    // Direct checkout buttons (some themes use links or buttons outside forms)
    document.querySelectorAll('a[href*="/checkout"], button[name="checkout"]').forEach(btn => {
      btn.removeEventListener('click', btnClickHandler);
      btn.addEventListener('click', btnClickHandler);
    });

    // "Buy it now" / Dynamic checkout buttons on product pages
    // Uses capture phase (true) to intercept before Shopify's own handler
    document.querySelectorAll('.shopify-payment-button__button').forEach(btn => {
      btn.removeEventListener('click', buyItNowClickHandler, true);
      btn.addEventListener('click', buyItNowClickHandler, true);
    });
  }

  function btnClickHandler(event) {
    handleCheckout(event, event.currentTarget);
  }

  // Attach on load and re-attach when cart drawer updates (Dawn uses custom elements)
  attachListeners();
  document.addEventListener('DOMContentLoaded', attachListeners);

  // Re-attach after cart drawer re-renders (Dawn dispatches this event)
  document.addEventListener('cart:updated', attachListeners);
  document.addEventListener('cart-drawer:open', attachListeners);

  // MutationObserver as fallback for dynamic cart elements
  const observer = new MutationObserver(() => attachListeners());
  observer.observe(document.body, { childList: true, subtree: true });
})();
